import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface PaginatedFetchParams<F extends Record<string, unknown>> {
  page: number;
  pageSize: number;
  search?: string;
  filters: F;
}

export interface PaginatedFetchResult<T> {
  data: T[];
  total: number;
}

export interface UsePaginatedDataConfig<T, F extends Record<string, unknown>> {
  fetcher: (params: PaginatedFetchParams<F>) => Promise<PaginatedFetchResult<T>>;
  initialPage?: number;
  initialPageSize?: number;
  initialSearch?: string;
  initialFilters?: F;
  enabled?: boolean;
  debounceMs?: number;
  keepPreviousData?: boolean;
}

export interface UsePaginatedDataResult<T, F extends Record<string, unknown>> {
  data: T[];
  total: number;
  loading: boolean;
  error: string | null;
  page: number;
  pageSize: number;
  search: string;
  appliedSearch: string;
  filters: F;
  appliedFilters: F;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  isEmpty: boolean;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  setSearch: (value: string) => void;
  setFilters: (filters: F) => void;
  refetch: (options?: { force?: boolean }) => Promise<void>;
}

export function usePaginatedData<T, F extends Record<string, unknown> = Record<string, never>>(
  config: UsePaginatedDataConfig<T, F>,
): UsePaginatedDataResult<T, F> {
  const {
    fetcher,
    initialPage = 1,
    initialPageSize = 25,
    initialSearch = '',
    initialFilters,
    enabled = true,
    debounceMs = 300,
    keepPreviousData = true,
  } = config;

  const [data, setData] = useState<T[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState<number>(initialPage);
  const [pageSize, setPageSize] = useState<number>(initialPageSize);
  const [search, setSearch] = useState<string>(initialSearch);
  const [appliedSearch, setAppliedSearch] = useState<string>(initialSearch);
  const [filters, setFilters] = useState<F>((initialFilters ?? {}) as F);
  const [appliedFilters, setAppliedFilters] = useState<F>((initialFilters ?? {}) as F);

  const latestParamsRef = useRef<PaginatedFetchParams<F> | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef<boolean>(false);

  const executeFetch = useCallback(
    async (params: PaginatedFetchParams<F>) => {
      if (!enabled) {
        return;
      }

      latestParamsRef.current = params;
      setLoading(true);

      try {
        const result = await fetcher(params);

        // Only update state if params are still current (avoid race conditions).
        if (latestParamsRef.current === params) {
          setData(result.data);
          setTotal(result.total);
          setError(null);
        }
      } catch (err) {
        console.error('usePaginatedData: failed to fetch', err);
        if (latestParamsRef.current === params) {
          setError(
            err instanceof Error ? err.message : 'Failed to load data. Please try again later.',
          );
          if (!keepPreviousData) {
            setData([]);
            setTotal(0);
          }
        }
      } finally {
        if (latestParamsRef.current === params) {
          setLoading(false);
        }
      }
    },
    [enabled, fetcher, keepPreviousData],
  );

  const scheduleFetch = useCallback(
    (params: PaginatedFetchParams<F>, immediate = false) => {
      if (!enabled) {
        setLoading(false);
        return;
      }

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      if (immediate || debounceMs === 0) {
        void executeFetch(params);
      } else {
        debounceRef.current = setTimeout(() => {
          debounceRef.current = null;
          void executeFetch(params);
        }, debounceMs);
      }
    },
    [debounceMs, enabled, executeFetch],
  );

  const buildParams = useCallback(
    (nextPage: number, nextPageSize: number, nextSearch: string, nextFilters: F) => ({
      page: nextPage,
      pageSize: nextPageSize,
      search: nextSearch ? nextSearch.trim() : undefined,
      filters: nextFilters,
    }),
    [],
  );

  const refetch = useCallback(
    async (options?: { force?: boolean }) => {
      const params = buildParams(page, pageSize, appliedSearch, appliedFilters);
      if (options?.force) {
        await executeFetch(params);
      } else {
        scheduleFetch(params, true);
      }
    },
    [appliedFilters, appliedSearch, buildParams, executeFetch, page, pageSize, scheduleFetch],
  );

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      setData([]);
      setTotal(0);
      return;
    }

    const params = buildParams(page, pageSize, appliedSearch, appliedFilters);
    scheduleFetch(params, !mountedRef.current);
    mountedRef.current = true;

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [
    appliedFilters,
    appliedSearch,
    buildParams,
    enabled,
    page,
    pageSize,
    scheduleFetch,
  ]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Apply debounced search term.
  useEffect(() => {
    if (search === appliedSearch) {
      return;
    }

    const handle = setTimeout(() => {
      setAppliedSearch(search);
      setPage(1);
    }, debounceMs);

    return () => clearTimeout(handle);
  }, [search, appliedSearch, debounceMs]);

  const setNextFilters = useCallback(
    (nextFilters: F) => {
      setFilters(nextFilters);
      setAppliedFilters(nextFilters);
      setPage(1);
    },
    [],
  );

  const value = useMemo<UsePaginatedDataResult<T, F>>(
    () => ({
      data,
      total,
      loading,
      error,
      page,
      pageSize,
      search,
      appliedSearch,
      filters,
      appliedFilters,
      hasNextPage: page * pageSize < total,
      hasPreviousPage: page > 1,
      isEmpty: !loading && total === 0,
      setPage,
      setPageSize: (nextPageSize: number) => {
        setPageSize(nextPageSize);
        setPage(1);
      },
      setSearch,
      setFilters: setNextFilters,
      refetch,
    }),
    [
      appliedFilters,
      appliedSearch,
      data,
      error,
      filters,
      loading,
      page,
      pageSize,
      refetch,
      search,
      setNextFilters,
      total,
    ],
  );

  return value;
}

export default usePaginatedData;
